import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Pagination from "@mui/material/Pagination";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import GameIcon from "components/admin/GameIcon";
import { itemTypeLabel } from "layouts/templates/labels";
import api from "api/client";

const PAGE_SIZE = 48;

/** PetRealmManager.ITEM_ADMIN: vật phẩm mở menu ADMIN, server chỉ cho tặng tài khoản admin. */
const ADMIN_ITEM_TYPE = -1;

/** Danh sách gọi một lần cho cả trang, lỗi thì lần sau gọi lại. */
function cached(path) {
  let cache = null;
  return () => {
    if (!cache) {
      cache = api.get(path).catch((e) => {
        cache = null;
        throw e;
      });
    }
    return cache;
  };
}

const loadItemTypes = cached("/api/admin/templates/item-types");
export const loadAchievements = cached("/api/admin/templates/achievements");
export const loadGiftGroups = cached("/api/admin/templates/gift-groups");

/** Bỏ dấu + chữ thường, giống SearchKey của máy chủ. */
const searchKey = (text) =>
  String(text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/gi, "d")
    .toLowerCase()
    .trim();

const SOURCES = {
  item: {
    title: "Chọn vật phẩm",
    fallback: "inventory_2",
    id: (x) => x.itemId,
    icon: (x) => x.iconPath,
    name: (x) => x.name,
    note: (x) =>
      `${itemTypeLabel(x.type)}${x.isStackable ? "" : ", không cộng dồn"}${
        x.description ? `. ${x.description}` : ""
      }`,
    remote: "/api/admin/templates/items",
  },
  pet: {
    title: "Chọn pet",
    fallback: "pets",
    id: (x) => x.petId,
    icon: (x) => x.icon,
    name: (x) => x.name,
    note: () => null,
    remote: "/api/admin/templates/pets",
  },
  title: {
    title: "Chọn danh hiệu",
    fallback: "military_tech",
    id: (x) => x.id,
    icon: (x) => x.iconPath,
    name: (x) => x.name,
    note: (x) => x.description,
    local: loadAchievements,
  },
  group: {
    title: "Chọn nhóm vật phẩm",
    fallback: "category",
    id: (x) => x.id,
    icon: (x) => x.items[0]?.iconPath,
    name: (x) => `${x.label} (${x.items.length} món)`,
    note: (x) => `Rơi 1 món bất kỳ: ${x.items.map((i) => i.name).join(", ")}`,
    local: loadGiftGroups,
  },
};

function fetchPage(source, { q, type, page, filter }) {
  if (source.local) {
    return source.local().then((list) => {
      const key = searchKey(q);
      const filtered = key
        ? list.filter((x) => String(source.id(x)) === q || searchKey(source.name(x)).includes(key))
        : list;
      const start = (page - 1) * PAGE_SIZE;
      return { items: filtered.slice(start, start + PAGE_SIZE), total: filtered.length };
    });
  }
  return api
    .get(source.remote, {
      q,
      type,
      page,
      size: PAGE_SIZE,
      types: filter?.types?.join(","),
      notTypes: filter?.notTypes?.join(","),
    })
    .then((res) => ({ items: res.items, total: res.total }));
}

function findOne(source, id) {
  if (source.local) {
    return source.local().then((list) => list.find((x) => source.id(x) === id) || null);
  }
  return api
    .get(source.remote, { q: String(id), size: 20 })
    .then((res) => res.items.find((x) => source.id(x) === id) || null);
}

/** Id, tên, icon của một dòng trong bảng chọn, theo loại nguồn (vật phẩm, pet, danh hiệu, nhóm). */
export const pickerId = (kind, x) => SOURCES[kind].id(x);
export const pickerName = (kind, x) => SOURCES[kind].name(x);
export const pickerIcon = (kind, x) => SOURCES[kind].icon(x);

/**
 * Lưới chọn có tìm kiếm, lọc loại vật phẩm và phân trang, nhúng được vào dialog khác.
 * Chọn một: onPick(x). Chọn nhiều: onToggle(x) và onTogglePage(items, đã chọn hết trang chưa).
 */
export function PickerBrowser({
  kind,
  itemType,
  itemFilter,
  hideAdminItems,
  multiple,
  selectedIds,
  existingIds,
  onPick,
  onToggle,
  onTogglePage,
  filterAsSelect,
}) {
  const source = SOURCES[kind];
  const [input, setInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState(null);
  const [itemTypes, setItemTypes] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const showTypes = kind === "item" && itemType == null;
  const type = itemType != null ? itemType : filterType;

  useEffect(() => {
    if (!showTypes) return;
    loadItemTypes()
      .then((res) =>
        setItemTypes(
          res.filter(
            (t) =>
              !(hideAdminItems && t.type === ADMIN_ITEM_TYPE) &&
              (!itemFilter?.types || itemFilter.types.includes(t.type)) &&
              !itemFilter?.notTypes?.includes(t.type)
          )
        )
      )
      .catch(() => setItemTypes([]));
  }, [showTypes]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(input.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [input]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetchPage(source, { q, type, page, filter: itemFilter })
      .then((res) => active && setData(res))
      .catch((e) => {
        if (!active) return;
        setError(e.message);
        setData({ items: [], total: 0 });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [kind, q, type, page]);

  const items = hideAdminItems ? data.items.filter((x) => x.type !== ADMIN_ITEM_TYPE) : data.items;
  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const pickType = (next) => {
    setFilterType(next);
    setPage(1);
  };
  const pageAllPicked = items.length > 0 && items.every((x) => selectedIds.has(source.id(x)));
  const typeOptions = [{ type: null, count: null }, ...itemTypes];
  const typeText = (t) => (t.type == null ? "Tất cả" : `${itemTypeLabel(t.type)} (${t.count})`);

  return (
    <MDBox display="flex" flexDirection="column" minWidth={0}>
      <MDBox pt={1} mb={2} display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <MDInput
          autoFocus
          label="Tìm theo tên hoặc id (không cần gõ dấu)"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          sx={{ flex: "1 1 240px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon>search</Icon>
              </InputAdornment>
            ),
          }}
        />
        {showTypes && filterAsSelect && (
          <MDInput
            select
            label="Loại vật phẩm"
            value={filterType ?? "all"}
            onChange={(event) => pickType(event.target.value === "all" ? null : event.target.value)}
            SelectProps={{ sx: { height: 44 } }}
            sx={{ flex: "0 1 220px" }}
          >
            {typeOptions.map((t) => (
              <MenuItem key={t.type ?? "all"} value={t.type ?? "all"}>
                {typeText(t)}
              </MenuItem>
            ))}
          </MDInput>
        )}
        {multiple && (
          <MDButton
            variant="outlined"
            color="info"
            size="small"
            disabled={loading || items.length === 0}
            onClick={() => onTogglePage(items, pageAllPicked)}
            sx={{ flexShrink: 0, height: 44 }}
          >
            <Icon>{pageAllPicked ? "deselect" : "select_all"}</Icon>&nbsp;
            {pageAllPicked ? "Bỏ cả trang" : "Chọn cả trang"}
          </MDButton>
        )}
      </MDBox>
      <MDBox display="flex" gap={2} flexDirection={{ xs: "column", md: "row" }}>
        {showTypes && !filterAsSelect && (
          <MDBox
            width={{ md: 200 }}
            flexShrink={0}
            maxHeight={{ xs: 120, md: 460 }}
            overflow="auto"
            display="flex"
            flexDirection={{ xs: "row", md: "column" }}
            flexWrap={{ xs: "wrap", md: "nowrap" }}
            gap={0.5}
          >
            {typeOptions.map((t) => {
              const active = t.type === filterType;
              return (
                <MDButton
                  key={t.type ?? "all"}
                  size="small"
                  variant={active ? "gradient" : "text"}
                  color={active ? "info" : "dark"}
                  onClick={() => pickType(t.type)}
                  sx={{ justifyContent: "flex-start", textTransform: "none", flexShrink: 0 }}
                >
                  {typeText(t)}
                </MDButton>
              );
            })}
          </MDBox>
        )}
        <MDBox flexGrow={1} minWidth={0} minHeight={320}>
          {error && (
            <MDTypography variant="caption" color="error" display="block" mb={1}>
              {error}
            </MDTypography>
          )}
          {loading ? (
            <MDBox display="flex" justifyContent="center" py={6}>
              <CircularProgress size={28} />
            </MDBox>
          ) : (
            <MDBox
              display="grid"
              gridTemplateColumns="repeat(auto-fill, minmax(120px, 1fr))"
              gap={1}
            >
              {items.map((x) => {
                const id = source.id(x);
                const selected = selectedIds.has(id);
                const already = existingIds.has(id);
                return (
                  <Tooltip key={id} title={source.note(x) || ""} disableInteractive>
                    <MDBox
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      textAlign="center"
                      gap={0.5}
                      p={1}
                      borderRadius="md"
                      position="relative"
                      onClick={() => (multiple ? onToggle(x) : onPick(x))}
                      sx={{
                        cursor: "pointer",
                        userSelect: "none",
                        border: "2px solid",
                        borderColor: selected ? "info.main" : "divider",
                        bgcolor: selected ? "action.selected" : "transparent",
                        "&:hover": { bgcolor: "action.hover", borderColor: "info.main" },
                      }}
                    >
                      {multiple && selected && (
                        <Icon
                          color="info"
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            fontSize: "18px !important",
                          }}
                        >
                          check_circle
                        </Icon>
                      )}
                      <GameIcon path={source.icon(x)} size={40} fallback={source.fallback} />
                      <MDTypography
                        variant="caption"
                        fontWeight="medium"
                        lineHeight={1.2}
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          wordBreak: "break-word",
                        }}
                      >
                        {source.name(x)}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ fontSize: 11 }}>
                        #{id}
                        {already && " · đã có"}
                      </MDTypography>
                    </MDBox>
                  </Tooltip>
                );
              })}
            </MDBox>
          )}
          {!loading && items.length === 0 && (
            <MDTypography variant="caption" color="text">
              Không tìm thấy
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      <MDBox display="flex" alignItems="center" gap={2} mt={1.5} flexWrap="wrap">
        <Pagination count={pages} page={page} onChange={(event, p) => setPage(p)} size="small" />
        <MDTypography variant="caption" color="text">
          {data.total} kết quả
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

PickerBrowser.defaultProps = {
  itemFilter: null,
  itemType: null,
  hideAdminItems: false,
  multiple: false,
  selectedIds: new Set(),
  existingIds: new Set(),
  onPick: () => {},
  onToggle: () => {},
  onTogglePage: () => {},
  filterAsSelect: false,
};
PickerBrowser.propTypes = {
  /** Giới hạn loại vật phẩm: { types: [...] } chỉ lấy các loại này, { notTypes: [...] } bỏ các loại này. */
  itemFilter: PropTypes.shape({
    types: PropTypes.arrayOf(PropTypes.number),
    notTypes: PropTypes.arrayOf(PropTypes.number),
  }),
  kind: PropTypes.oneOf(Object.keys(SOURCES)).isRequired,
  itemType: PropTypes.number,
  hideAdminItems: PropTypes.bool,
  multiple: PropTypes.bool,
  selectedIds: PropTypes.instanceOf(Set),
  existingIds: PropTypes.instanceOf(Set),
  onPick: PropTypes.func,
  onToggle: PropTypes.func,
  onTogglePage: PropTypes.func,
  /** Lọc loại vật phẩm bằng ô thả xuống thay vì cột bên trái, dùng khi nhúng vào dialog đã có cột. */
  filterAsSelect: PropTypes.bool,
};

export function PickerDialog({
  kind,
  value,
  itemType,
  itemFilter,
  hideAdminItems,
  onPick,
  onClose,
  multiple,
  existing,
  amountLabel,
  onConfirm,
}) {
  const source = SOURCES[kind];
  const [picked, setPicked] = useState([]);
  const [amount, setAmount] = useState(1);
  const pickedIds = new Set(picked.map((x) => source.id(x)));
  const selectedIds = multiple ? pickedIds : new Set(value ? [Number(value)] : []);
  const existingIds = new Set(multiple ? (existing || []).map(Number) : []);
  const toggle = (x) => {
    const id = source.id(x);
    setPicked((prev) =>
      prev.some((p) => source.id(p) === id) ? prev.filter((p) => source.id(p) !== id) : [...prev, x]
    );
  };
  const togglePage = (items, allPicked) =>
    setPicked((prev) => {
      if (allPicked) {
        const ids = new Set(items.map((x) => source.id(x)));
        return prev.filter((p) => !ids.has(source.id(p)));
      }
      const have = new Set(prev.map((p) => source.id(p)));
      return [...prev, ...items.filter((x) => !have.has(source.id(x)))];
    });
  const confirm = () => {
    if (picked.length) onConfirm(picked, Math.max(1, Number(amount) || 1));
  };
  const showTypes = kind === "item" && itemType == null;

  return (
    <Dialog open onClose={onClose} maxWidth={showTypes ? "lg" : "md"} fullWidth>
      <DialogTitle>
        {source.title}
        {multiple && (
          <MDTypography variant="caption" color="text" display="block">
            Bấm nhiều món để chọn cùng lúc, chuyển trang hay tìm kiếm vẫn giữ các món đã chọn
          </MDTypography>
        )}
      </DialogTitle>
      <DialogContent>
        <PickerBrowser
          kind={kind}
          itemType={itemType}
          itemFilter={itemFilter}
          hideAdminItems={hideAdminItems}
          multiple={multiple}
          selectedIds={selectedIds}
          existingIds={existingIds}
          onPick={onPick}
          onToggle={toggle}
          onTogglePage={togglePage}
        />
      </DialogContent>
      {multiple && (
        <MDBox px={3} pt={1} borderTop="1px solid" borderColor="divider">
          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
            <MDTypography variant="caption" fontWeight="medium">
              Đã chọn {picked.length} món
            </MDTypography>
            {picked.length > 0 && (
              <MDButton variant="text" color="error" size="small" onClick={() => setPicked([])}>
                Bỏ chọn hết
              </MDButton>
            )}
          </MDBox>
          <MDBox display="flex" flexWrap="wrap" gap={0.5} maxHeight={96} overflow="auto">
            {picked.map((x) => (
              <Chip
                key={source.id(x)}
                size="small"
                variant="outlined"
                label={`${source.name(x)} #${source.id(x)}`}
                icon={
                  <MDBox display="inline-flex" pl={0.5}>
                    <GameIcon path={source.icon(x)} size={18} fallback={source.fallback} />
                  </MDBox>
                }
                onDelete={() => toggle(x)}
              />
            ))}
          </MDBox>
        </MDBox>
      )}
      <DialogActions sx={{ gap: 1 }}>
        {multiple && amountLabel && (
          <MDInput
            type="number"
            size="small"
            label={amountLabel}
            value={amount}
            inputProps={{ min: 1 }}
            onChange={(event) => setAmount(event.target.value)}
            sx={{ width: 150 }}
          />
        )}
        <MDButton variant="text" color="secondary" onClick={onClose}>
          {multiple ? "Huỷ" : "Đóng"}
        </MDButton>
        {multiple && (
          <MDButton variant="gradient" color="info" disabled={!picked.length} onClick={confirm}>
            <Icon>add</Icon>&nbsp;Thêm {picked.length || ""} món
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

PickerDialog.defaultProps = {
  itemFilter: null,
  value: null,
  itemType: null,
  hideAdminItems: false,
  onPick: () => {},
  multiple: false,
  existing: [],
  amountLabel: null,
  onConfirm: () => {},
};
PickerDialog.propTypes = {
  itemFilter: PickerBrowser.propTypes.itemFilter,
  kind: PropTypes.oneOf(Object.keys(SOURCES)).isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  itemType: PropTypes.number,
  hideAdminItems: PropTypes.bool,
  onPick: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  /** Chọn nhiều món rồi bấm "Thêm", gọi onConfirm(danh sách object, số lượng chung). */
  multiple: PropTypes.bool,
  /** Id đã có sẵn, chỉ đánh dấu "đã có" chứ không chặn chọn. */
  existing: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  /** Có thì hiện ô số lượng chung cho các món chọn ở chế độ nhiều. */
  amountLabel: PropTypes.string,
  onConfirm: PropTypes.func,
};

/**
 * Ô chọn vật phẩm / pet / danh hiệu / nhóm vật phẩm: bấm vào mở bảng có icon, tìm kiếm, lọc theo loại
 * và phân trang thay vì danh sách thả xuống. value là id (số) hoặc null, onChange nhận (id, object).
 */
function ModalPicker({
  kind,
  label,
  value,
  onChange,
  itemType,
  itemFilter,
  hideAdminItems,
  item,
  onLoad,
}) {
  const source = SOURCES[kind];
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(item);

  useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    const id = Number(value);
    if (selected && source.id(selected) === id) return undefined;
    if (item && source.id(item) === id) {
      setSelected(item);
      return undefined;
    }
    let active = true;
    findOne(source, id)
      .then((x) => {
        if (!active) return;
        setSelected(x);
        onLoad(x);
      })
      .catch(() => active && setSelected(null));
    return () => {
      active = false;
    };
  }, [value, kind]);

  let text = "Bấm để chọn";
  if (selected) text = `${source.name(selected)} (#${source.id(selected)})`;
  else if (value) text = `#${value}`;

  return (
    <>
      <MDBox
        role="button"
        tabIndex={0}
        display="flex"
        alignItems="center"
        gap={1}
        px={1.5}
        py={0.5}
        minHeight={44}
        borderRadius="md"
        onClick={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(true);
          }
        }}
        sx={{
          cursor: "pointer",
          border: "1px solid",
          borderColor: "divider",
          borderStyle: value ? "solid" : "dashed",
          "&:hover, &:focus-visible": { borderColor: "info.main", outline: "none" },
        }}
      >
        <GameIcon
          path={selected ? source.icon(selected) : null}
          size={28}
          fallback={source.fallback}
        />
        <MDBox flexGrow={1} minWidth={0} lineHeight={1.2}>
          {label && (
            <MDTypography variant="caption" color="text" display="block">
              {label}
            </MDTypography>
          )}
          <MDTypography
            variant="button"
            fontWeight={selected ? "medium" : "regular"}
            color={value ? "dark" : "text"}
            display="block"
            noWrap
          >
            {text}
          </MDTypography>
        </MDBox>
        <Icon color="info">{value ? "swap_horiz" : "search"}</Icon>
      </MDBox>
      {open && (
        <PickerDialog
          kind={kind}
          value={value}
          itemType={itemType}
          itemFilter={itemFilter}
          hideAdminItems={hideAdminItems}
          onClose={() => setOpen(false)}
          onPick={(x) => {
            setSelected(x);
            setOpen(false);
            onChange(source.id(x), x);
          }}
        />
      )}
    </>
  );
}

ModalPicker.defaultProps = {
  onLoad: () => {},
  itemFilter: null,
  item: null,
  label: "",
  value: null,
  itemType: null,
  hideAdminItems: false,
};

ModalPicker.propTypes = {
  /** Gọi với object của món khi ô tự tải từ máy chủ (value có sẵn từ trước, chưa truyền item). */
  onLoad: PropTypes.func,
  itemFilter: PickerBrowser.propTypes.itemFilter,
  kind: PropTypes.oneOf(Object.keys(SOURCES)).isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  /** Khoá vào một loại vật phẩm (vd. 17 = ngọc khảm); không truyền thì có cột lọc theo loại. */
  itemType: PropTypes.number,
  /** Ẩn vật phẩm admin (loại -1), dùng khi người nhận không phải admin. */
  hideAdminItems: PropTypes.bool,
  /** Object đã có sẵn (vd. vừa chọn ở bảng chọn nhiều), khỏi gọi máy chủ lấy lại tên. */
  item: PropTypes.shape({}),
};

export default ModalPicker;
